import { makeStyles } from '@material-ui/core/styles';
import { Handle, Position } from '@xyflow/react';
import classnames from 'classnames';
import hexToRgba from 'hex-rgba';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import { ModalType } from '../../app/types';
import IconDatabase from '../../assets/images/Icons/IconDatabase';
import icFlow from '../../assets/images/svg/20_ic_flow_20.svg';
import icDelete20 from '../../assets/images/svg/ic-delete-20.svg';
import {
  deleteNode,
  setIsShowSettingBar,
  setSelectedNode,
} from '../../features/mainSlice';
import { openModal } from '../../features/modalSlice';
import Tooltip from '../shared/Tooltip';

const useStyles = makeStyles(
  {
    root: {
      width: 240,
      borderRadius: '12px',
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      border: 0,
    },
    selected: {
      border: `solid 1px ${color.$23}`,
    },
    highlightPath: {
      border: `solid 1px ${color.$3}!important`,
    },
    handle: {
      border: '0 !important',
      borderRadius: '0 !important',
      background: `${color.$3} !important`,
      width: '1px !important',
      height: '1px !important',
      minWidth: '1px !important',
      minHeight: '1px !important',
    },
    title: {
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$23,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 14px 8px 16px',
      background: hexToRgba(color.$23, 6),
      '& div': {
        display: 'flex',
        alignItems: 'center',
      },
      '& img': {
        marginRight: 2,
      },
    },
    content: {
      padding: '12px 16px',
    },
    name: {
      fontSize: '15px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      marginBottom: 12,
      '& > img': {
        marginRight: 5,
      },
    },
    procedure: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 8px',
      background: color.$19,
      '& > img': {
        marginRight: 4,
      },
      '& > div': {
        maxWidth: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
    },
    icon: {
      marginRight: 4,
    },
  },
  {
    name: 'MA',
  },
);

const MAComponent = ({ id, data, isConnectable }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isActive = useSelector(store => store.ma.isActive);
  const list = useSelector(store => store.ma.list);
  const selectedNode = useSelector(store => store.ma.selectedNode);
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const allPathNodes = useSelector(store => store.ma.highlightPathNodes) || [];
  const isPreview = useSelector(store => store.ma.isPreview);
  const node = useMemo(() => nodes.find(n => n?.id === id), [nodes]);
  const [isHover, setIsHover] = useState(false);

  const isHighlightPath = isPreview && !!allPathNodes.find(n => n?.id === id);
  const isSelected = selectedNode?.id === id;

  const { procedureId } = data || {};
  const { name } = useMemo(
    () => list.find(n => n?.id === procedureId) || {},
    [list],
  );

  function onDelete() {
    dispatch(
      openModal({
        modalType: ModalType.DIALOG_TEXT,
        modalProps: {
          title: t('delete-ma-node-modal.title'),
          content: t('delete-ma-node-modal.content'),
          cancelText: t('delete-ma-node-modal.cancelText'),
          submitText: t('delete-ma-node-modal.submitText'),
        },
        onClick: () => {
          dispatch(deleteNode(id));
        },
      }),
    );
  }

  const onSelect = () => {
    if (!isSelected) {
      dispatch(setSelectedNode(node));
    }

    dispatch(setIsShowSettingBar(true));
  };

  return (
    <div>
      <Handle
        className={classes.handle}
        type="target"
        position={Position.Top}
        style={{ top: 0 }}
        isConnectable={isConnectable}
      />
      <div
        className={classnames(classes.root, {
          [classes.selected]: isSelected,
          [classes.highlightPath]: isHighlightPath,
        })}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={onSelect}
      >
        <div className={classes.title}>
          <div>
            <IconDatabase
              className={classes.icon}
              backgroundColor={color.$23}
            />
            {t('add-ma')}
          </div>
          {isHover && !isActive && (
            <img
              style={{ cursor: 'pointer' }}
              onClick={() => onDelete()}
              src={icDelete20}
              alt="delete"
            />
          )}
        </div>
        <div className={classes.content}>
          <div className={classes.procedure}>
            <img src={icFlow} alt="procedure" />
            <Tooltip light placement="top" title={name}>
              <div>{name}</div>
            </Tooltip>
          </div>
        </div>
      </div>
      <Handle
        className={classes.handle}
        type="source"
        position={Position.Bottom}
        style={{ bottom: 0 }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default MAComponent;
